import React from 'react';
import { Outlet } from 'react-router-dom';
import { ThemeProvider } from '../providers/ThemeProvider';
import { ToastProvider } from '../components/ui/Toast';
import { KodiProvider } from '../providers/KodiProvider';
import { OrgProvider } from '../providers/OrgProvider';
import { SettingsProvider } from '../providers/SettingsProvider';
import { AlertProvider } from '../providers/AlertProvider';
import { AlertToastProvider } from '../providers/AlertToastProvider';
import { FeatureFlagProvider } from '../providers/FeatureFlagProvider';
import { LiveFeedProvider } from '../providers/LiveFeedProvider';
import { AudioProvider } from '../providers/AudioProvider';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { MainLayout } from './MainLayout';
import { HeaderBannerProvider } from '../hooks/useHeaderBanner';

/**
 * AppShell - Root layout for all V2 routes.
 *
 * This component stays mounted while navigating between pages,
 * ensuring all providers and state persist.
 *
 * Provider stack (outer to inner):
 * - ThemeProvider: Light/dark mode
 * - ErrorBoundary: Catches errors and shows fallback UI
 * - FeatureFlagProvider: Feature flags from PostHog
 * - ToastProvider: Global notifications
 * - OrgProvider: Current org selection and channels (requires AuthProvider above)
 * - SettingsProvider: Users, speakers, tags, invitations (requires OrgProvider)
 * - LiveFeedProvider: Real-time message state for live feed feature
 * - AudioProvider: Always-mounted live audio playback (persists across navigation)
 * - AlertToastProvider: Client-side, real-time toasts for saved-filter alerts
 *   (matches the live message stream; only fires while LiveFeedProvider is subscribed)
 * - KodiProvider: AI assistant state
 * - AlertProvider: Alert keyword matching for inline highlighting
 * - MainLayout: Visual layout (Header, Sidebar)
 * - ErrorBoundary (page): Catches page render errors without losing providers/layout
 * - Outlet: Current page content
 */
export const AppShell: React.FC = () => {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <FeatureFlagProvider>
          <ToastProvider position="bottom-right">
            <OrgProvider>
              <SettingsProvider>
                <LiveFeedProvider>
                  <AudioProvider>
                    <AlertToastProvider>
                      <KodiProvider>
                        <AlertProvider>
                          <HeaderBannerProvider>
                            <MainLayout>
                              <ErrorBoundary>
                                <Outlet />
                              </ErrorBoundary>
                            </MainLayout>
                          </HeaderBannerProvider>
                        </AlertProvider>
                      </KodiProvider>
                    </AlertToastProvider>
                  </AudioProvider>
                </LiveFeedProvider>
              </SettingsProvider>
            </OrgProvider>
          </ToastProvider>
        </FeatureFlagProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};
